import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LinkedinLogo,
  InstagramLogo,
  FacebookLogo,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { BRAND, SERVICES, SOCIAL_LINKS } from "../lib/constants";

const SOCIAL_ICONS = {
  LinkedinLogo,
  InstagramLogo,
  FacebookLogo,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  async function subscribe(e) {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Check your inbox for updates.");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 409) toast.error("You're already subscribed.");
      else toast.error("Couldn't subscribe. Try again.");
    } finally {
      setSubscribing(false);
    }
  }

  return (
    <footer data-testid="main-footer" className="relative bg-brand-navy-darker border-t border-white/5 pt-20 pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* CTA Banner */}
        <div className="glass-card rounded-3xl p-8 lg:p-12 mb-20 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="eyebrow font-mono-accent text-[11px] uppercase tracking-[0.25em] mb-3">
              / Ready when you are
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tighter leading-[1.05]">
              Let's build something <br />
              <span className="text-brand-mint-light italic font-normal">worth keeping.</span>
            </h2>
          </div>
          <div className="flex lg:justify-end">
            <Link
              to="/book-consultation"
              data-testid="footer-cta-button"
              className="group inline-flex items-center gap-2 bg-brand-mint text-brand-navy-darker hover:bg-brand-mint-light transition rounded-full px-7 py-4 font-semibold"
            >
              Book a Free Consultation
              <ArrowUpRight size={18} weight="bold" className="group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img src={BRAND.logo} alt="Gtechnohubb" className="h-11 w-11 rounded-md" />
              <div>
                <div className="font-display text-white text-lg">GTECHNOHUBB</div>
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-brand-mint">New Gen Technology</div>
              </div>
            </div>
            <p className="text-brand-slate text-sm leading-relaxed max-w-xs">
              Digital transformation, senior-led engineering, and global delivery — since 2013.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {SOCIAL_LINKS.map(({ label, href, icon }) => {
                const Icon = SOCIAL_ICONS[icon];
                return (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${label}`}
                    aria-label={label}
                    className="rounded-full border border-white/10 p-2.5 text-white/70 hover:text-brand-mint hover:border-brand-mint/40 transition"
                  >
                    <Icon size={16} weight="bold" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-brand-mint mb-5">Services</div>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-white/70 hover:text-brand-mint text-sm transition" data-testid={`footer-service-${s.slug}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-brand-mint mb-5">Company</div>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-white/70 hover:text-brand-mint text-sm transition" data-testid="footer-about">About</Link></li>
              <li><Link to="/industries" className="text-white/70 hover:text-brand-mint text-sm transition" data-testid="footer-industries">Industries</Link></li>
              <li><Link to="/services" className="text-white/70 hover:text-brand-mint text-sm transition" data-testid="footer-services">All Services</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-brand-mint text-sm transition" data-testid="footer-contact">Contact</Link></li>
            </ul>
          </div>

          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-brand-mint mb-5">Newsletter</div>
            <p className="text-white/70 text-sm mb-4">Monthly insights on engineering, AI, and healthcare IT.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-input"
                className="flex-1 bg-transparent border border-white/20 focus:border-brand-mint rounded-full px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none transition"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-submit"
                className="bg-brand-mint text-brand-navy-darker rounded-full p-2.5 hover:bg-brand-mint-light transition disabled:opacity-50"
                aria-label="Subscribe"
              >
                <ArrowUpRight size={16} weight="bold" />
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs font-mono-accent uppercase tracking-wider">
            © {new Date().getFullYear()} Gtechnohubb. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-white/50 font-mono-accent uppercase tracking-wider">
            <a href="#" className="hover:text-brand-mint transition" data-testid="footer-privacy">Privacy</a>
            <a href="#" className="hover:text-brand-mint transition" data-testid="footer-terms">Terms</a>
            <a href="#" className="hover:text-brand-mint transition" data-testid="footer-cookies">Cookies</a>
          </div>
        </div>
      </div>

      {/* Giant brand name — full-width with side padding for a professional look */}
      <div className="w-full overflow-hidden select-none mt-10 -mb-4 px-6 sm:px-10 lg:px-16">
        <div className="font-display leading-[0.85] tracking-tighter text-white/5 text-center whitespace-nowrap" style={{ fontSize: "clamp(2.5rem, 12vw, 18rem)" }}>
          GTECHNOHUBB
        </div>
      </div>
    </footer>
  );
}
